import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowRight, Eye, EyeSlash, Info } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import LanguageToggle from "../components/layout/LanguageToggle";
import { useAuth } from "../contexts/AuthContext";

const LOGO_MARK = "/logomark.png";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const schema = z.object({
    email: z
      .string()
      .trim()
      .email(t("student_portal.errors.email_invalid")),
    password: z.string().min(1, t("student_portal.errors.password_required")),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = useCallback(
    async ({ email, password }) => {
      setServerError("");
      setSubmitting(true);
      // One transparent retry on transient errors (network / 5xx) to absorb
      // ingress hiccups and dev-mode backend reloads.
      const attempt = async () => login(email, password);
      try {
        let u;
        try {
          u = await attempt();
        } catch (err) {
          const transient = !err.status || err.status >= 500;
          if (!transient) throw err;
          await new Promise((r) => setTimeout(r, 600));
          u = await attempt();
        }
        if (u && u.role === "admin") {
          navigate("/admin", { replace: true });
        } else {
          navigate("/portal", { replace: true });
        }
      } catch (err) {
        if (err.status === 429) {
          setServerError(t("student_portal.errors.rate_limit"));
        } else if (err.status === 401) {
          setServerError(t("student_portal.errors.invalid_credentials"));
        } else {
          setServerError(t("student_portal.errors.generic"));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [login, navigate, t],
  );

  return (
    <main
      data-testid="login-page"
      className="relative min-h-screen flex items-center justify-center bg-slate-50 overflow-hidden py-16 px-6"
    >
      <div className="absolute inset-0 adonis-grid-bg [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
      <div
        className="absolute top-1/3 left-1/2 -translate-x-1/2 h-[420px] w-[680px] opacity-20 blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, #7B61FF 0%, transparent 60%), radial-gradient(circle at 30% 50%, #4DD0E1 0%, transparent 60%)",
        }}
      />

      {/* Top-right language toggle (portal has no header) */}
      <div className="absolute top-6 right-6 z-10">
        <LanguageToggle />
      </div>

      <div className="relative w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Link to="/" data-testid="login-back-home" className="inline-flex items-center gap-3">
            <img src={LOGO_MARK} alt="Adonis Agency" className="h-10 w-10" />
            <span className="font-heading text-lg font-bold tracking-tight text-slate-900">
              Adonis<span className="text-slate-400 font-medium"> Agency</span>
            </span>
          </Link>
        </div>

        <div className="rounded-3xl border-2 border-slate-200 bg-white p-8 md:p-10 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.12)]">
          <div className="text-center mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
              {t("student_portal.title")}
            </h1>
            <p className="mt-3 text-sm text-slate-600 max-w-xs mx-auto">
              {t("student_portal.subtitle")}
            </p>
          </div>

          <form
            data-testid="login-form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="space-y-5"
          >
            <div>
              <Label
                htmlFor="email"
                className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700"
              >
                {t("student_portal.email_label")}
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                autoComplete="email"
                autoFocus
                placeholder={t("student_portal.email_placeholder")}
                className="mt-2 h-12 rounded-xl border-slate-200"
                {...register("email")}
              />
              {errors.email && (
                <p className="mt-1.5 text-xs text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <div className="flex items-baseline justify-between">
                <Label
                  htmlFor="password"
                  className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700"
                >
                  {t("student_portal.password_label")}
                </Label>
                <button
                  type="button"
                  data-testid="login-forgot"
                  onClick={() => setShowForgot((v) => !v)}
                  className="text-[11px] font-medium text-slate-500 hover:text-slate-900 transition-colors"
                >
                  {t("student_portal.forgot_link")}
                </button>
              </div>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  data-testid="login-password"
                  autoComplete="current-password"
                  placeholder={t("student_portal.password_placeholder")}
                  className="h-12 rounded-xl border-slate-200 pr-12"
                  {...register("password")}
                />
                <button
                  type="button"
                  data-testid="login-toggle-password"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 px-4 flex items-center text-slate-400 hover:text-slate-700 transition-colors"
                  aria-label={
                    showPassword
                      ? t("student_portal.hide_password")
                      : t("student_portal.show_password")
                  }
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-xs text-red-600">
                  {errors.password.message}
                </p>
              )}
            </div>

            {showForgot && (
              <div
                data-testid="login-forgot-info"
                className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex gap-3 text-sm text-slate-700"
              >
                <Info size={16} weight="bold" className="shrink-0 mt-0.5 text-slate-500" />
                <p className="leading-relaxed">
                  {t("student_portal.forgot_info")}{" "}
                  <Link
                    to="/contact"
                    className="font-medium text-slate-900 underline underline-offset-4 decoration-slate-300 hover:decoration-slate-900 transition-colors"
                  >
                    {t("student_portal.forgot_cta")}
                  </Link>
                </p>
              </div>
            )}

            {serverError && (
              <div
                data-testid="login-error"
                className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {serverError}
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit"
              disabled={submitting}
              className="group w-full inline-flex items-center justify-center gap-2 rounded-full adonis-gradient px-7 py-4 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#7B61FF]/30 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {submitting ? t("student_portal.submitting") : t("student_portal.submit")}
              {!submitting && (
                <ArrowRight
                  size={14}
                  weight="bold"
                  className="transition-transform group-hover:translate-x-1"
                />
              )}
            </button>
          </form>

          <p className="mt-8 text-center text-xs text-slate-500 leading-relaxed">
            {t("student_portal.no_signup")}
          </p>
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          <Link
            to="/contact"
            className="text-slate-600 hover:text-slate-900 underline underline-offset-4 decoration-slate-300 transition-colors"
          >
            {t("student_portal.contact_admin")}
          </Link>
        </p>
      </div>
    </main>
  );
}
